import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { dataApi, api, authedUrl } from "@/lib/api";
import { PageHeader, SectionCard, KpiCard } from "@/components/Primitives";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Gauge, CalendarCheck, CheckCircle2, Code, Star, TrendingUp, Sparkles, GitCommit, GitPullRequest, MessageSquare } from "lucide-react";
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

export default function EmployeeProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [git, setGit] = useState(null);

  useEffect(() => {
    dataApi.employeeAnalytics(id).then((r) => setData(r.data));
    api.get(`/git/employees/${id}?days=90`).then((r) => setGit(r.data)).catch(() => {});
  }, [id]);

  if (!data) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const e = data.employee;
  const k = data.kpis;
  const radar = [
    { skill: "Productivity", A: k.productivity },
    { skill: "Attendance", A: k.attendance },
    { skill: "Tasks", A: k.task_completion },
    { skill: "Code Quality", A: k.code_quality },
    { skill: "Reviews", A: k.review_score },
    { skill: "Velocity", A: Math.min(k.velocity * 2.5, 100) },
  ];

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => nav("/employees")}><ArrowLeft className="w-4 h-4 mr-1" />Back</Button>

      <div className="glass rounded-2xl p-6 flex flex-col md:flex-row md:items-center gap-6">
        <Avatar className="h-24 w-24">
          <AvatarImage src={authedUrl(e.avatar_url)} />
          <AvatarFallback className="text-2xl">{e.full_name.split(" ").map((s) => s[0]).slice(0, 2).join("")}</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display font-semibold text-3xl tracking-tight">{e.full_name}</h1>
            <Badge className="capitalize">{e.role.replace("_", " ")}</Badge>
          </div>
          <div className="text-muted-foreground mt-1">{e.designation} · {e.department} · {e.location}</div>
          <div className="text-sm text-muted-foreground mt-0.5">{e.email}</div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {(e.skills || []).map((s) => <Badge key={s} variant="secondary" className="text-xs font-normal">{s}</Badge>)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard testid="kpi-prod" label="Productivity" value={k.productivity} suffix="%" icon={Gauge} tone="primary" />
        <KpiCard testid="kpi-att" label="Attendance" value={k.attendance} suffix="%" icon={CalendarCheck} tone="success" />
        <KpiCard testid="kpi-tasks" label="Task Completion" value={k.task_completion} suffix="%" icon={CheckCircle2} />
        <KpiCard testid="kpi-code" label="Code Quality" value={k.code_quality} icon={Code} />
        <KpiCard testid="kpi-review" label="Reviews" value={k.review_score} icon={Star} />
        <KpiCard testid="kpi-velo" label="Velocity" value={k.velocity} icon={TrendingUp} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList data-testid="emp-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="git" data-testid="tab-git">Git Activity</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai">AI Summary</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="pt-6 grid lg:grid-cols-2 gap-6">
          <SectionCard title="Performance Radar" subtitle="Multi-dimension scoring">
            <div className="h-64">
              <ResponsiveContainer>
                <RadarChart data={radar}>
                  <PolarGrid stroke="hsl(var(--border))" />
                  <PolarAngleAxis dataKey="skill" tick={{ fontSize: 11 }} />
                  <Radar dataKey="A" stroke="hsl(211 100% 55%)" fill="hsl(211 100% 55%)" fillOpacity={0.3} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </SectionCard>
          <SectionCard title="Task Summary">
            <div className="grid grid-cols-2 gap-4">
              <div><div className="label-caps">Total</div><div className="number text-3xl">{data.task_summary.total}</div></div>
              <div><div className="label-caps">Completed</div><div className="number text-3xl text-emerald-500">{data.task_summary.completed}</div></div>
              <div><div className="label-caps">In progress</div><div className="number text-3xl text-primary">{data.task_summary.in_progress}</div></div>
              <div><div className="label-caps">Todo</div><div className="number text-3xl text-muted-foreground">{data.task_summary.todo}</div></div>
            </div>
          </SectionCard>
        </TabsContent>

        <TabsContent value="tasks" className="pt-6">
          <SectionCard title="Assigned Tasks">
            <div className="text-sm text-muted-foreground">Total {data.task_summary.total} tasks · {data.task_summary.completed} completed.</div>
          </SectionCard>
        </TabsContent>

        <TabsContent value="attendance" className="pt-6">
          <SectionCard title="Recent Attendance">
            <div className="space-y-1 text-sm">
              {(data.recent_attendance || []).slice(0, 14).map((a) => (
                <div key={a.id} className="flex items-center gap-3 py-1">
                  <span className="number text-muted-foreground text-xs w-24">{a.date}</span>
                  <Badge variant={a.status === "present" ? "default" : "secondary"} className="capitalize">{a.status.replace("_", " ")}</Badge>
                  {a.is_late && <Badge variant="destructive" className="text-[10px]">Late</Badge>}
                  <span className="ml-auto text-xs text-muted-foreground">{a.total_hours} hrs</span>
                </div>
              ))}
            </div>
          </SectionCard>
        </TabsContent>

        <TabsContent value="git" className="pt-6 space-y-6">
          {git ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard label="Commits (90d)" value={git.totals.commits} icon={GitCommit} tone="primary" />
                <KpiCard label="Pull Requests" value={git.totals.pull_requests} icon={GitPullRequest} />
                <KpiCard label="Reviews" value={git.totals.reviews} icon={MessageSquare} />
                <KpiCard label="Lines +/-" value={`${git.totals.lines_added}/${git.totals.lines_removed}`} icon={Code} />
              </div>
              <SectionCard title="Git Activity Trend" subtitle="Last 90 days">
                <div className="h-64">
                  <ResponsiveContainer>
                    <BarChart data={git.trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="commits" fill="hsl(211 100% 55%)" name="Commits" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="prs" fill="hsl(262 83% 62%)" name="PRs" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="reviews" fill="hsl(32 95% 55%)" name="Reviews" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </SectionCard>
              <SectionCard title="Per Repository" subtitle="Contribution across all repos">
                <div className="space-y-2">
                  {git.per_repo.map((r, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-border/40 last:border-0">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{r.repository.name || r.repository.id}</div>
                        <div className="text-[11px] text-muted-foreground capitalize">{r.repository.platform || "unknown"}</div>
                      </div>
                      <div className="text-right"><div className="number text-sm">{r.commits}</div><div className="text-[9px] text-muted-foreground">commits</div></div>
                      <div className="text-right"><div className="number text-sm">{r.prs}</div><div className="text-[9px] text-muted-foreground">PRs</div></div>
                      <div className="text-right"><div className="number text-sm">{r.reviews}</div><div className="text-[9px] text-muted-foreground">reviews</div></div>
                      <div className="text-right"><div className="number text-sm text-emerald-500">+{r.additions}</div><div className="number text-sm text-red-500">-{r.deletions}</div></div>
                    </div>
                  ))}
                  {git.per_repo.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">No git activity yet.</div>}
                </div>
              </SectionCard>
              <SectionCard title="Recent Commits">
                <div className="space-y-1">
                  {git.recent_commits.slice(0, 12).map((c) => (
                    <div key={c.id} className="flex items-center gap-3 py-1.5 text-sm">
                      <code className="text-[10px] text-muted-foreground font-mono w-16">{c.sha.slice(0, 7)}</code>
                      <span className="flex-1 truncate">{c.message}</span>
                      <span className="text-[10px] text-emerald-500 number">+{c.additions}</span>
                      <span className="text-[10px] text-red-500 number">-{c.deletions}</span>
                      <span className="text-[10px] text-muted-foreground number w-24 text-right">{c.committed_at.slice(0, 10)}</span>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </>
          ) : (
            <div className="text-sm text-muted-foreground">Loading git activity…</div>
          )}
        </TabsContent>

        <TabsContent value="ai" className="pt-6">
          <SectionCard title="AI Summary" subtitle="Generated from performance metrics">
            <div className="space-y-3 text-sm">
              <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
                <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-primary" /><span className="font-medium">Strengths</span></div>
                <div className="text-muted-foreground">{e.full_name} demonstrates strong {k.code_quality > 80 ? "code quality" : "engineering discipline"} and consistent {k.attendance > 90 ? "attendance" : "delivery"}.</div>
              </div>
              <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20">
                <div className="font-medium mb-1">Growth Areas</div>
                <div className="text-muted-foreground">Consider mentorship in {k.velocity < 20 ? "sprint velocity" : "code reviews"} and cross-team collaboration.</div>
              </div>
              <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                <div className="font-medium mb-1">Promotion Readiness</div>
                <div className="text-muted-foreground">{k.productivity > 85 ? "High" : k.productivity > 70 ? "Moderate" : "Developing"} — {Math.round((k.productivity + k.attendance + k.task_completion) / 3)}% composite score.</div>
              </div>
            </div>
          </SectionCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
